import { arrayMove } from "@dnd-kit/sortable";
import { PlusIcon } from "@phosphor-icons/react";
import { createId } from "@paralleldrive/cuid2";
import { defaultSections } from "@reactive-resume/schema";
import type { Experience } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";
import get from "lodash.get";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";

import { formatExperiencePeriod } from "./experience/date-utils";
import { ExperienceForm } from "./experience/experience-form";
import { ExperienceList } from "./experience/experience-list";

type View = "list" | "form";

type ExperienceSection = {
  name: string;
  items: Experience[];
};

type ExperienceWithContract = Experience & {
  contractType?: string;
};

export const ExperienceInlineSection = () => {
  const { toast } = useToast();

  const setValue = useResumeStore((state) => state.setValue);

  const section = useResumeStore(
    (state) =>
      get(
        state.resume.data.sections,
        "experience",
        defaultSections.experience,
      ) as ExperienceSection,
  );

  const items = section.items ?? [];

  const [view, setView] = useState<View>(() => (items.length ? "list" : "form"));

  const [editingItem, setEditingItem] = useState<Experience>();

  const openCreate = () => {
    setEditingItem(undefined);
    setView("form");
  };

  const openEdit = (item: Experience) => {
    setEditingItem(item);
    setView("form");
  };

  const closeForm = () => {
    setEditingItem(undefined);

    setView(items.length ? "list" : "form");
  };

  const save = (values: Experience) => {
    const wasEditing = Boolean(editingItem);

    const payload: ExperienceWithContract = {
      ...values,
      id: editingItem?.id ?? createId(),
      visible: editingItem?.visible ?? true,
      date: formatExperiencePeriod(
        values.startDate ?? "",
        values.endDate ?? "",
        Boolean(values.isPresent),
      ),
    };

    const nextItems = editingItem
      ? items.map((item) => (item.id === editingItem.id ? payload : item))
      : [...items, payload];

    setValue("sections.experience.items", nextItems);

    setEditingItem(undefined);
    setView("list");

    toast({
      variant: "success",
      title: wasEditing ? "Expérience mise à jour" : "Expérience ajoutée",
    });
  };

  const remove = (id: string) => {
    const nextItems = items.filter((item) => item.id !== id);

    setValue("sections.experience.items", nextItems);

    if (nextItems.length === 0) {
      setEditingItem(undefined);

      setView("form");
    }

    toast({
      variant: "success",
      title: "Expérience supprimée",
    });
  };

  const reorder = (activeId: string, overId: string) => {
    if (activeId === overId) {
      return;
    }

    const oldIndex = items.findIndex((item) => item.id === activeId);

    const newIndex = items.findIndex((item) => item.id === overId);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    setValue("sections.experience.items", arrayMove(items, oldIndex, newIndex));
  };

  return (
    <section id="experience" className="mx-auto w-full max-w-[720px] pb-3">
      <header
        className={cn("flex flex-col gap-3", "sm:flex-row sm:items-start", "sm:justify-between")}
      >
        <div>
          <p
            className={cn("text-[10px] font-bold uppercase", "tracking-[0.14em] text-emerald-600")}
          >
            Expérience
          </p>

          <h1
            className={cn(
              "mt-1.5 text-2xl font-bold",
              "tracking-tight text-indigo-950",
              "sm:text-[28px]",
            )}
          >
            Votre parcours professionnel
          </h1>

          <p className={cn("mt-1.5 max-w-lg", "text-xs leading-5 text-slate-500", "sm:text-sm")}>
            Transformez vos responsabilités et vos missions en réalisations faciles à comprendre.
          </p>
        </div>

        {view === "list" && (
          <button
            type="button"
            onClick={openCreate}
            className={cn(
              "inline-flex min-h-10 w-fit",
              "items-center gap-2 rounded-xl",
              "bg-indigo-700 px-4",
              "text-xs font-bold text-white",
              "shadow-sm transition-colors",
              "hover:bg-indigo-800",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            )}
          >
            <PlusIcon aria-hidden size={15} weight="bold" />
            Ajouter
          </button>
        )}
      </header>

      <div
        role="tablist"
        aria-label="Gestion des expériences"
        className="mt-5 flex rounded-xl bg-slate-100 p-1"
      >
        <button
          type="button"
          role="tab"
          aria-selected={view === "list"}
          disabled={items.length === 0}
          onClick={() => {
            setEditingItem(undefined);

            setView("list");
          }}
          className={cn(
            "min-h-9 flex-1 rounded-lg",
            "text-[11px] font-bold",
            "transition-colors",
            view === "list"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-indigo-700",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          Expériences ajoutées
          <span className={cn("ml-1 rounded-full", "bg-indigo-50 px-1.5 py-0.5", "text-[9px]")}>
            {items.length}
          </span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={view === "form"}
          onClick={editingItem ? undefined : openCreate}
          className={cn(
            "min-h-9 flex-1 rounded-lg",
            "text-[11px] font-bold",
            "transition-colors",
            view === "form"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-indigo-700",
          )}
        >
          {editingItem ? "Modifier l’expérience" : "Nouvelle expérience"}
        </button>
      </div>

      <div className="mt-4">
        {view === "form" ? (
          <ExperienceForm item={editingItem} onCancel={closeForm} onSave={save} />
        ) : (
          <ExperienceList
            items={items}
            onAdd={openCreate}
            onEdit={openEdit}
            onDelete={remove}
            onReorder={reorder}
          />
        )}
      </div>
    </section>
  );
};
